import { useEffect, useMemo, useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  ArrowsClockwiseIcon,
  FlagIcon,
  PaperPlaneTiltIcon,
  SparkleIcon,
  SpinnerIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TextInput } from '@/components/common/TextInput';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { HtmlWYSIWYGEditor } from '@/components/common/HtmlWYSIWYGEditor';
import { EmailRecipientsPanel } from '@/components/common/email-composer/EmailRecipientsPanel';
import {
  EMAIL_REGEX,
  dedupeContactsByEmail,
  makeRecipientId,
  type CustomRecipient,
  type EmailComposerContact,
  type RecipientRole,
} from '@/components/common/email-composer/types';
import {
  invalidateThreadQueries,
  useArCollectionThread,
} from '@/api/rpc/hooks/accounts-receivable/useArCollectionThreads';
import { useQueryClient } from '@tanstack/react-query';
import { useArCustomerContacts } from '@/api/rpc/hooks/accounts-receivable/useArCustomerContacts';
import { useDraftArCollectionEmail } from '@/api/rpc/hooks/accounts-receivable/useArCollectionEmailDrafts';
import { useOrganizationContacts } from '@/api/rpc/hooks/communications/useOrganizationContacts';
import { useArCustomerCommsNotes } from '@/api/rpc/hooks/accounts-receivable/useArCustomerCommsNotes';
import { AttachmentsPanel } from './AttachmentsPanel';
import { useGetModuleConfig } from '@/api/org/config/modules';
import {
  AR_DEFAULT_COLLECTIONS_INTERNAL_ONLY_ENABLED,
  AR_DEFAULT_INTERNAL_ONLY_DOMAINS,
  ProductModule,
} from '@erp/shared-types';
import {
  useEmailDraft,
  useSendEmailDraft,
} from '@/api/rpc/hooks/communications/useEmailDraft';
import useAuth from '@/api/auth/useAuth';
import { useToast } from 'src/context/ToastContext';
import type {
  CollectionEmailClassification,
  CollectionEmailClassificationCategory,
  CollectionEmailClassificationConfidence,
  EmailImportance,
} from '@erp/rpc';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  threadId: string;
  threadTitle: string;
  /**
   * When provided, the dialog opens in manual mode and drafts from this prompt
   * on mount instead of auto-classifying. Used by the recommendation card to
   * seed a draft from a specific recommended action. The prompt also pre-fills
   * the "Regenerate with custom prompt" box so the user can tweak and re-run.
   */
  initialPrompt?: string;
  /**
   * Email addresses to seed as TO recipients on the first draft, in addition
   * to whatever the backend draft carries (currently always empty for
   * collection drafts). Used by the `email_sales_person` action path to
   * pre-populate the salesperson's address; the user can still edit.
   */
  initialToRecipients?: string[];
}

const CATEGORY_LABELS: Record<CollectionEmailClassificationCategory, string> = {
  dispute_invalid: 'Dispute invalid',
  accept_short_pay: 'Accept short pay',
  request_remittance_clarification: 'Request remittance clarification',
  request_documentation: 'Request documentation',
  escalate: 'Escalate',
  manual_user_directed: 'Manual',
};

const CONFIDENCE_TONE: Record<
  CollectionEmailClassificationConfidence,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  high: 'default',
  medium: 'secondary',
  low: 'outline',
  none: 'outline',
};

export function CollectionEmailDraftDialog({
  open,
  onOpenChange,
  threadId,
  threadTitle,
  initialPrompt,
  initialToRecipients,
}: Props) {
  // The body owns all per-draft state; remounting it on open or threadId
  // change is what gives us a fresh slate, instead of a reset effect that
  // synchronously calls setState across nine pieces of state. The
  // `open && threadId` gate also keeps the AI mutation from auto-firing
  // while the dialog is closed. The key folds in initialPrompt so reopening
  // from a different recommendation re-seeds the body even for the same thread.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-[95vw] lg:max-w-6xl max-h-[90vh] flex flex-col">
        {open && threadId && (
          <DraftDialogBody
            key={`${threadId}:${initialPrompt ?? ''}`}
            chrome="dialog"
            threadId={threadId}
            threadTitle={threadTitle}
            initialPrompt={initialPrompt}
            initialToRecipients={initialToRecipients}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Inline (non-modal) variant of the composer, rendered directly inside the
 * collection thread view so the clerk can read thread documents while drafting.
 * It shares the exact same body/state as {@link CollectionEmailDraftDialog} —
 * only the surrounding chrome differs. The parent owns which draft is active and
 * is responsible for keying this component on `${threadId}:${initialPrompt}` so
 * switching to a different recommendation re-seeds the body from scratch, the
 * same way the dialog's `key` does.
 */
export function CollectionEmailDraftPanel({
  threadId,
  threadTitle,
  initialPrompt,
  initialToRecipients,
  onClose,
}: {
  threadId: string;
  threadTitle: string;
  initialPrompt?: string;
  initialToRecipients?: string[];
  /** Collapse the inline section (called on Cancel and after a successful send). */
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-primary/30 bg-card shadow-sm">
      <DraftDialogBody
        chrome="inline"
        threadId={threadId}
        threadTitle={threadTitle}
        initialPrompt={initialPrompt}
        initialToRecipients={initialToRecipients}
        onClose={onClose}
      />
    </div>
  );
}

function DraftDialogBody({
  chrome,
  threadId,
  threadTitle,
  initialPrompt,
  initialToRecipients,
  onClose,
}: {
  /**
   * Which surrounding chrome to render. `dialog` uses the shadcn Dialog
   * header/footer (the modal); `inline` renders a plain card header + footer so
   * the same body can live directly in the thread view.
   */
  chrome: 'dialog' | 'inline';
  threadId: string;
  threadTitle: string;
  initialPrompt?: string;
  initialToRecipients?: string[];
  onClose: () => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const senderEmail = currentUser?.emails[0] ?? null;
  const { thread } = useArCollectionThread(threadId);
  const customerId = thread?.customerId ?? null;

  const { contacts: customerContacts, isLoading: customerContactsLoading } =
    useArCustomerContacts(customerId ?? '');
  // Also autocomplete against the org "address book" (internal contacts, e.g.
  // salespeople) — surfaced alongside the customer's own contacts.
  const { contacts: orgContacts, isLoading: orgContactsLoading } =
    useOrganizationContacts();
  const contactsLoading = customerContactsLoading || orgContactsLoading;

  // "Send From" options — mirror BulkSendStatementDialog so AR clerks can
  // choose between the configured AR outbox and their own mailbox. Each
  // option carries a resolved `mailboxUpn` that we pass to the backend on
  // draft creation and again on send (the server re-authorizes it).
  const { data: arModuleConfig } = useGetModuleConfig(
    ProductModule.AR_INVOICING,
  );
  type SendFromOption = {
    key: string;
    label: string;
    mailboxUpn: string;
  };
  const sendFromOptions = useMemo<SendFromOption[]>(() => {
    const options: SendFromOption[] = [];
    const arConfig = arModuleConfig?.config?.ar;
    if (arConfig?.outboxMailboxUpn) {
      options.push({
        key: 'configured',
        label: arConfig.outboxSenderAddress ?? arConfig.outboxMailboxUpn,
        mailboxUpn: arConfig.outboxMailboxUpn,
      });
    }
    const userEmail = currentUser?.emails[0];
    if (userEmail) {
      options.push({
        key: `user:${userEmail.toLowerCase()}`,
        label: userEmail,
        mailboxUpn: userEmail,
      });
    }
    return options;
  }, [arModuleConfig, currentUser]);
  const [selectedSendFromKey, setSelectedSendFromKey] = useState('');
  useEffect(() => {
    if (sendFromOptions.length === 0) {
      setSelectedSendFromKey('');
      return;
    }
    if (!sendFromOptions.some((o) => o.key === selectedSendFromKey)) {
      setSelectedSendFromKey(sendFromOptions[0].key);
    }
  }, [sendFromOptions, selectedSendFromKey]);
  const selectedMailboxUpn = useMemo(
    () =>
      sendFromOptions.find((o) => o.key === selectedSendFromKey)?.mailboxUpn,
    [sendFromOptions, selectedSendFromKey],
  );

  // Recipient state — owned here so the panel stays presentational.
  const [selectedEmails, setSelectedEmails] = useState<
    Map<string, RecipientRole>
  >(new Map());
  // Seed action-driven TO recipients (e.g. the salesperson for an
  // `email_sales_person` recommendation) into initial state so they appear in
  // the TO row on first render — before the AI draft finishes generating —
  // instead of only after the draft loads. The draft-load effect below rebuilds
  // this list, folding in whatever recipients the draft itself carries.
  const [customRecipients, setCustomRecipients] = useState<CustomRecipient[]>(
    () =>
      buildCustomRecipients(
        (initialToRecipients ?? []).map((email) => recipientEntry(email, 'to')),
      ),
  );
  // "BCC me" defaults off. Opt-in is per draft and deliberately not persisted —
  // the dialog remounts per thread, so a stored default would be the only way to
  // make it sticky and we don't want it sticky.
  const [bccSender, setBccSender] = useState(false);

  // AI draft state
  const [draftId, setDraftId] = useState<string | null>(null);
  const [classification, setClassification] =
    useState<CollectionEmailClassification | null>(null);
  // Seed the intake prompt from initialPrompt so a recommendation-driven
  // draft runs with the recommended intent.
  const [userPrompt, setUserPrompt] = useState(initialPrompt ?? '');
  // Rewrite feedback for regenerating the current draft. Deliberately its own
  // state (not the intake prompt) and cleared after each regenerate, so text
  // typed in one box never resurfaces in the other.
  const [steerPrompt, setSteerPrompt] = useState('');
  // Opt-in: persist this steer as a standing note so future drafts/recs for
  // this customer pick it up.
  const [rememberNote, setRememberNote] = useState(false);
  const { append: appendCommsNote } = useArCustomerCommsNotes(customerId ?? '');

  // Intake gate. A seeded prompt (from a recommendation) drafts immediately,
  // so it starts already "started". The top-button path starts un-started and
  // shows a choice — auto-draft vs. describe-it — before any generation runs.
  const [started, setStarted] = useState(Boolean(initialPrompt?.trim()));
  const [intakeView, setIntakeView] = useState<'choice' | 'describe'>('choice');

  // Subject + body local edits (seeded from the fetched draft).
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  // Priority flag for the outgoing email. Modeled as the full Graph importance
  // enum but surfaced as a simple high-priority toggle; seeded from the draft.
  const [importance, setImportance] = useState<EmailImportance>('normal');
  // Documents the clerk has selected to attach. Flushed to the draft row on
  // Send. Seeded from the server copy on the first draft that loads in this
  // dialog open and then owned locally — regenerate creates a fresh draft
  // with an empty attachment list, but the clerk's choices shouldn't be wiped
  // just because the AI rewrote the body.
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<string[]>(
    [],
  );
  const [hasSeededAttachments, setHasSeededAttachments] = useState(false);
  // Recipients are seeded once (like attachments) then clerk-owned, so a
  // regenerate — or the first draft arriving after the clerk edited recipients
  // mid-generation — never wipes their choices.
  const [hasSeededRecipients, setHasSeededRecipients] = useState(false);
  // `HtmlWYSIWYGEditor` only consumes `initialHtml` on mount, so we have to
  // delay mounting it until *after* the seed effect has populated htmlBody.
  // This flag flips in the same batch as `setHtmlBody(draft.htmlContent)`,
  // so the first render that mounts the editor sees the seeded body.
  const [seededDraftId, setSeededDraftId] = useState<string | null>(null);

  const draftMutation = useDraftArCollectionEmail();
  const { draft, isLoading: isDraftLoading } = useEmailDraft(
    draftId ?? undefined,
  );
  const { updateAndSend, isPending: isSending } = useSendEmailDraft();

  // Auto-fire the AI draft on mount — but only for a seeded prompt (drafting a
  // specific recommendation in manual mode). The top-button path has no seed
  // and instead waits for the user to choose auto vs. describe-it via the
  // intake, so it must NOT fire here. The `isActive` flag protects against a
  // stale in-flight response setting state on an unmounted body when the user
  // closes the dialog mid-fetch. We wait for `selectedMailboxUpn` because the
  // draft is created with a real mailbox UPN — without it the server would 400.
  useEffect(() => {
    if (!selectedMailboxUpn) return;
    const seeded = initialPrompt?.trim();
    if (!seeded) return;
    let isActive = true;
    void (async () => {
      try {
        const result = await draftMutation.mutateAsync({
          threadId,
          mode: 'manual',
          userPrompt: seeded,
          sendingMailbox: selectedMailboxUpn,
        });
        if (!isActive) return;
        setDraftId(result.draftId);
        setClassification(result.classification);
      } catch (err) {
        if (!isActive) return;
        showToast({
          title: 'Failed to draft email',
          message: err instanceof Error ? err.message : 'Unknown error',
          color: 'error',
        });
      }
    })();
    return () => {
      isActive = false;
    };
    // Re-fire only when the mailbox becomes available — the body is already
    // remounted on threadId change via the outer `key`, so threadId isn't
    // needed here. Switching Send From mid-edit must NOT regenerate the
    // body; the choice is re-applied at send-time instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMailboxUpn != null]);

  // Kick off the first draft from the top-button intake. `auto` runs the
  // classify-and-draft pass; `manual` drafts from the intent the user typed.
  async function startDraft(mode: 'auto' | 'manual') {
    if (!selectedMailboxUpn) return;
    setStarted(true);
    try {
      const result =
        mode === 'manual'
          ? await draftMutation.mutateAsync({
              threadId,
              mode: 'manual',
              userPrompt: userPrompt.trim(),
              sendingMailbox: selectedMailboxUpn,
            })
          : await draftMutation.mutateAsync({
              threadId,
              mode: 'auto',
              sendingMailbox: selectedMailboxUpn,
            });
      setDraftId(result.draftId);
      setClassification(result.classification);
    } catch (err) {
      showToast({
        title: 'Failed to draft email',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'error',
      });
    }
  }

  // Seed editable fields when the draft loads. Regenerate replaces the
  // draftId so the same effect re-seeds for the new draft. `setSeededDraftId`
  // batches with `setHtmlBody` so the gated BodyEditor mounts with the
  // populated body on the same render.
  useEffect(() => {
    if (!draft) return;
    setSubject(draft.subject);
    setHtmlBody(draft.htmlContent ?? '');
    // Seed attachment selection + priority on the FIRST draft that loads in
    // this dialog open. Subsequent drafts (regenerate) create a new draftId
    // with an empty server-side selection and default priority — we
    // deliberately keep the clerk's choices instead of resetting them.
    if (!hasSeededAttachments) {
      setSelectedAttachmentIds(draft.documentAttachmentIds);
      setImportance(draft.importance);
      setHasSeededAttachments(true);
    }
    // Fold the draft's own recipients into the list on the FIRST draft load,
    // then leave recipients clerk-owned (mirrors the attachment seeding above).
    // Preserving `prev` keeps the action-seeded salesperson AND anything the
    // clerk added while generation was in flight — and a regenerate won't wipe
    // them. Drafts return `to: []` today; fold in to/cc if the backend ever
    // populates them, skipping addresses already present (first role wins).
    if (!hasSeededRecipients) {
      setCustomRecipients((prev) => {
        const present = new Set(prev.map((r) => r.email.trim().toLowerCase()));
        const additions = buildCustomRecipients([
          ...splitEmails(draft.to).map((email) => recipientEntry(email, 'to')),
          ...splitEmails(draft.cc ?? '').map((email) =>
            recipientEntry(email, 'cc'),
          ),
        ]).filter((r) => !present.has(r.email.trim().toLowerCase()));
        return additions.length > 0 ? [...prev, ...additions] : prev;
      });
      setHasSeededRecipients(true);
    }
    setSeededDraftId(draft.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id]);

  // Contact list for the recipients panel: the customer's own contacts merged
  // with the org address book, normalized + deduped by email. Customer contacts
  // come first so their identity/tags win on any shared address; address-book
  // contacts carry no inline tags.
  const composerContacts: EmailComposerContact[] = useMemo(
    () =>
      dedupeContactsByEmail([
        ...customerContacts,
        // Address-book contacts carry no inline tags; annotate the shape so the
        // empty tags array isn't widened and no cast is needed.
        ...orgContacts.map(
          (
            c,
          ): {
            id: string;
            email: string | null;
            tags: { name: string }[];
          } => ({
            id: c.id,
            email: c.email,
            tags: [],
          }),
        ),
      ]),
    [customerContacts, orgContacts],
  );

  // ── Recipient panel callbacks ────────────────────────────────────────────

  function addContactToRole(email: string, role: RecipientRole) {
    setSelectedEmails((prev) => {
      if (prev.get(email) === role) return prev;
      const next = new Map(prev);
      next.set(email, role);
      return next;
    });
  }

  function toggleEmail(email: string) {
    setSelectedEmails((prev) => {
      const next = new Map(prev);
      if (next.has(email)) {
        next.delete(email);
        if (![...next.values()].includes('to') && next.size > 0) {
          const firstKey = next.keys().next().value;
          if (firstKey !== undefined) next.set(firstKey, 'to');
        }
      } else {
        next.set(email, next.size === 0 ? 'to' : 'cc');
      }
      return next;
    });
  }

  function toggleEmailRole(email: string) {
    setSelectedEmails((prev) => {
      const next = new Map(prev);
      const current = next.get(email);
      if (!current) return prev;
      if (
        current === 'to' &&
        [...prev.values()].filter((v) => v === 'to').length === 1
      ) {
        return prev;
      }
      next.set(email, current === 'to' ? 'cc' : 'to');
      return next;
    });
  }

  function removeContact(email: string) {
    setSelectedEmails((prev) => {
      if (!prev.has(email)) return prev;
      const next = new Map(prev);
      next.delete(email);
      if (![...next.values()].includes('to') && next.size > 0) {
        const firstKey = next.keys().next().value;
        if (firstKey !== undefined) next.set(firstKey, 'to');
      }
      return next;
    });
  }

  function addCustomRecipient(email: string, role: RecipientRole) {
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) return false;
    const lower = trimmed.toLowerCase();
    if (selectedEmails.has(lower)) return false;
    if (customRecipients.some((r) => r.email.toLowerCase() === lower)) {
      return false;
    }
    setCustomRecipients((prev) => [
      ...prev,
      { id: makeRecipientId(), email: trimmed, role },
    ]);
    return true;
  }

  function removeCustomRecipient(id: string) {
    setCustomRecipients((prev) => prev.filter((r) => r.id !== id));
  }

  // ── Regenerate / send ────────────────────────────────────────────────────

  async function handleRegenerate(mode: 'auto' | 'manual') {
    // `manual` is the feedback composer's attached "Rewrite" action and
    // always sends the composer text; `auto` is the body-header "Regenerate
    // draft" button, which lives away from the composer precisely because it
    // never reads it. Split the call so the discriminated input schema
    // accepts each branch — `manual` requires a non-empty userPrompt at the
    // type level.
    const steer = steerPrompt.trim();
    if (mode === 'manual' && !steer) return;
    try {
      const result =
        mode === 'manual'
          ? await draftMutation.mutateAsync({
              threadId,
              mode: 'manual',
              userPrompt: steer,
              sendingMailbox: selectedMailboxUpn,
              // Send the draft as currently shown — including any hand edits
              // to subject/body — so the rewrite revises the existing text
              // instead of starting over.
              currentDraft: { subject, htmlBody },
            })
          : await draftMutation.mutateAsync({
              threadId,
              mode: 'auto',
              sendingMailbox: selectedMailboxUpn,
            });
      setDraftId(result.draftId);
      setClassification(result.classification);
      // The feedback was consumed by this rewrite — clear it so it can't
      // silently ride along on the next one.
      if (mode === 'manual') setSteerPrompt('');

      // Persist the steer as a standing comms note when opted in. Best-effort:
      // the draft already succeeded, so a note-save failure shouldn't surface
      // as a regenerate failure — just flag it.
      if (mode === 'manual' && rememberNote && steer.length > 0 && customerId) {
        try {
          await appendCommsNote(steer);
          setRememberNote(false);
          showToast({
            title: 'Saved to comms notes',
            message:
              'This guidance will inform future drafts for this customer.',
            color: 'success',
          });
        } catch (noteErr) {
          showToast({
            title: 'Draft updated, but note not saved',
            message:
              noteErr instanceof Error ? noteErr.message : 'Unknown error',
            color: 'error',
          });
        }
      }
    } catch (err) {
      showToast({
        title: 'Failed to regenerate draft',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'error',
      });
    }
  }

  const { toCsv, ccCsv, bccCsv } = useMemo(
    () =>
      collectRecipientCsv(
        selectedEmails,
        customRecipients,
        bccSender ? senderEmail : null,
      ),
    [selectedEmails, customRecipients, bccSender, senderEmail],
  );

  // Org-level safety gate: while the AR collections "Send to Internal only"
  // toggle is on (ar.collections.internalOnlyEnabled, fail-safe on when
  // unset), this dialog may only send to addresses on the AR module's
  // internal-domain allow-list. The user can still add external addresses
  // (Send is what blocks), so they see the offending entries before having
  // to clean them up. The server enforces the same gate at send time.
  const externalRecipientsAllowed = !(
    arModuleConfig?.config?.ar?.collections?.internalOnlyEnabled ??
    AR_DEFAULT_COLLECTIONS_INTERNAL_ONLY_ENABLED
  );
  const internalDomains = useMemo(() => {
    const raw =
      arModuleConfig?.config?.ar?.internalOnlyDomains ??
      AR_DEFAULT_INTERNAL_ONLY_DOMAINS;
    return raw
      .map((d) => d.trim().replace(/^@/, '').toLowerCase())
      .filter((d) => d.length > 0);
  }, [arModuleConfig]);
  const externalOffenders = useMemo(() => {
    if (externalRecipientsAllowed) return [];
    return collectExternalOffenders(toCsv, ccCsv, bccCsv, internalDomains);
  }, [externalRecipientsAllowed, toCsv, ccCsv, bccCsv, internalDomains]);
  const blockedByDomainGate = externalOffenders.length > 0;

  async function handleSend() {
    if (!draftId) return;
    if (!toCsv) {
      showToast({
        title: 'Recipient required',
        message: 'Add at least one TO recipient before sending.',
        color: 'error',
      });
      return;
    }
    if (blockedByDomainGate) {
      showToast({
        title: 'External recipients blocked',
        message: `Cannot send to ${externalOffenders.join(
          ', ',
        )} — outside the allowed internal domains (${internalDomains.join(
          ', ',
        )}).`,
        color: 'error',
      });
      return;
    }
    try {
      await updateAndSend(draftId, {
        to: toCsv,
        cc: ccCsv || undefined,
        bcc: bccCsv || undefined,
        subject,
        htmlContent: htmlBody,
        // Reroute the persisted draft to the currently-picked mailbox.
        // The handler authorizes any change vs. the existing draft's
        // mailbox, so re-sending the same UPN is a cheap no-op.
        sendingMailbox: selectedMailboxUpn,
        documentAttachmentIds: selectedAttachmentIds,
        importance,
      });
      // The send appended an activity to the thread server-side; refetch so
      // the timeline shows it without a manual refresh.
      invalidateThreadQueries(queryClient);
      onClose();
    } catch {
      // useSendEmailDraft surfaces its own toast.
    }
  }

  const isGenerating = draftMutation.isPending;
  const isBusy = isGenerating || isDraftLoading || isSending;

  const footerButtons = (
    <>
      <Button variant="outline" onClick={onClose} disabled={isSending}>
        <T context="button, cancel email draft">Cancel</T>
      </Button>
      <Button
        onClick={handleSend}
        disabled={isBusy || !draftId || blockedByDomainGate}
      >
        {isSending ? (
          <SpinnerIcon className="h-4 w-4 mr-1 animate-spin" />
        ) : (
          <PaperPlaneTiltIcon className="h-4 w-4 mr-1" />
        )}
        <T context="button, send email draft">Send</T>
      </Button>
    </>
  );

  return (
    <>
      {chrome === 'dialog' ? (
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title, draft collection email">
              Draft collection email
            </T>
          </DialogTitle>
          <div className="text-xs text-muted-foreground truncate">
            <Var>{threadTitle}</Var>
          </div>
        </DialogHeader>
      ) : (
        <div className="flex items-start justify-between gap-2 border-b bg-muted/30 px-4 py-2.5">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-sm font-semibold">
              <SparkleIcon className="h-4 w-4 text-primary" />
              <T context="panel title, draft collection email">
                Draft collection email
              </T>
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {threadTitle}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="-mr-1 h-7 w-7 flex-shrink-0 p-0 text-muted-foreground"
            onClick={onClose}
            aria-label={gt('Close draft', {
              context: 'button, close the inline email composer',
            })}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div
        className={
          chrome === 'dialog'
            ? 'flex-1 min-h-0 overflow-y-auto pr-1 space-y-3'
            : 'space-y-3 px-4 py-3'
        }
      >
        {!started ? (
          <DraftIntake
            view={intakeView}
            userPrompt={userPrompt}
            onUserPromptChange={setUserPrompt}
            onPickDescribe={() => setIntakeView('describe')}
            onBack={() => setIntakeView('choice')}
            onAuto={() => void startDraft('auto')}
            onDescribe={() => void startDraft('manual')}
            mailboxReady={selectedMailboxUpn != null}
          />
        ) : (
          <>
            <ClassificationBanner
              isGenerating={isGenerating && !classification}
              classification={classification}
            />

            <EmailRecipientsPanel
              contacts={composerContacts}
              contactsLoading={contactsLoading}
              selectedEmails={selectedEmails}
              customRecipients={customRecipients}
              addCustomRecipient={addCustomRecipient}
              addContactToRole={addContactToRole}
              removeCustomRecipient={removeCustomRecipient}
              removeContact={removeContact}
              toggleEmail={toggleEmail}
              toggleEmailRole={toggleEmailRole}
              bccSender={bccSender}
              setBccSender={setBccSender}
              senderEmail={senderEmail}
              sendFromOptions={sendFromOptions}
              selectedSendFromKey={selectedSendFromKey}
              setSelectedSendFromKey={setSelectedSendFromKey}
            />

            <SubjectRow
              value={subject}
              onChange={setSubject}
              disabled={isBusy || !draftId}
            />

            <PriorityRow
              importance={importance}
              onChange={setImportance}
              disabled={isBusy || !draftId}
            />

            <div className="space-y-1">
              <div className="flex items-center justify-between px-1">
                <span className="text-xs font-medium text-muted-foreground">
                  <T context="form label, email body">Body</T>
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs text-muted-foreground"
                  onClick={() => void handleRegenerate('auto')}
                  disabled={isBusy || !draftId}
                >
                  <ArrowsClockwiseIcon className="h-3.5 w-3.5 mr-1" />
                  <T context="button, regenerate the email draft from scratch">
                    Regenerate draft
                  </T>
                </Button>
              </div>
              <BodyEditor
                draftId={draftId}
                htmlBody={htmlBody}
                onChangeHtml={setHtmlBody}
                isLoading={!draftId || seededDraftId !== draftId}
                isGenerating={isGenerating}
              />
            </div>

            <AttachmentsPanel
              threadId={threadId}
              selectedIds={selectedAttachmentIds}
              onSelectedIdsChange={setSelectedAttachmentIds}
              disabled={isBusy}
            />

            <RewriteFeedbackRow
              userPrompt={steerPrompt}
              onUserPromptChange={setSteerPrompt}
              onRewrite={() => void handleRegenerate('manual')}
              rememberNote={rememberNote}
              onRememberNoteChange={setRememberNote}
              canRemember={customerId !== null}
              isBusy={isBusy}
            />

            {blockedByDomainGate && (
              <ExternalRecipientsBlockedBanner
                offenders={externalOffenders}
                allowedDomains={internalDomains}
              />
            )}
          </>
        )}
      </div>

      {chrome === 'dialog' ? (
        <DialogFooter>{footerButtons}</DialogFooter>
      ) : (
        <div className="flex justify-end gap-2 border-t bg-muted/20 px-4 py-2.5">
          {footerButtons}
        </div>
      )}
    </>
  );
}

// ── Subviews ─────────────────────────────────────────────────────────────────

function ClassificationBanner({
  isGenerating,
  classification,
}: {
  isGenerating: boolean;
  classification: CollectionEmailClassification | null;
}) {
  if (isGenerating) {
    return (
      <div className="rounded-md border bg-muted/30 px-3 py-2 flex items-center gap-2 text-sm">
        <SpinnerIcon className="h-4 w-4 animate-spin text-muted-foreground" />
        <T context="status, generating ai draft">
          Generating draft and classifying thread…
        </T>
      </div>
    );
  }
  if (!classification) return null;
  return (
    <div className="rounded-md border bg-card px-3 py-2 space-y-1.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <T context="section title, AI reasoning for email draft classification">
          AI reasoning
        </T>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          variant={CONFIDENCE_TONE[classification.confidence]}
          className="pointer-events-none"
        >
          {CATEGORY_LABELS[classification.category]}
        </Badge>
        <Badge variant="outline" className="text-[10px] pointer-events-none">
          <T context="confidence label">Confidence</T>:{' '}
          <Var>{classification.confidence}</Var>
        </Badge>
        {classification.needsHumanReview && (
          <Badge
            variant="destructive"
            className="text-[10px] pointer-events-none"
          >
            <WarningIcon className="h-3 w-3 mr-0.5" />
            <T context="badge, needs human review">Review</T>
          </Badge>
        )}
      </div>
      <div className="text-xs text-muted-foreground">
        {classification.reasoning}
      </div>
    </div>
  );
}

function SubjectRow({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 px-1">
      <span className="shrink-0 text-xs font-medium text-muted-foreground">
        <T context="email header Subject field">Subject:</T>
      </span>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="h-8 text-sm"
      />
    </div>
  );
}

function PriorityRow({
  importance,
  onChange,
  disabled,
}: {
  importance: EmailImportance;
  onChange: (importance: EmailImportance) => void;
  disabled?: boolean;
}) {
  const isHigh = importance === 'high';
  return (
    <label className="flex items-center gap-2 px-1 text-xs cursor-pointer select-none">
      <Checkbox
        checked={isHigh}
        onCheckedChange={(v) => onChange(v === true ? 'high' : 'normal')}
        disabled={disabled}
      />
      <FlagIcon
        weight={isHigh ? 'fill' : 'regular'}
        className={`h-3.5 w-3.5 ${
          isHigh ? 'text-destructive' : 'text-muted-foreground'
        }`}
      />
      <span
        className={
          isHigh ? 'font-medium text-destructive' : 'text-muted-foreground'
        }
      >
        <T context="checkbox, mark outgoing email as high priority">
          Mark as high priority
        </T>
      </span>
    </label>
  );
}

function BodyEditor({
  draftId,
  htmlBody,
  onChangeHtml,
  isLoading,
  isGenerating,
}: {
  draftId: string | null;
  htmlBody: string;
  onChangeHtml: (html: string) => void;
  isLoading: boolean;
  isGenerating: boolean;
}) {
  if (isLoading || !draftId) {
    return (
      <div className="relative h-64 w-full">
        <Skeleton className="absolute inset-0 h-full w-full" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
          <SpinnerIcon className="h-6 w-6 animate-spin" />
          <span>
            {isGenerating ? (
              <T context="status, waiting for AI to draft email body">
                Generating draft with AI…
              </T>
            ) : (
              <T context="status, loading saved email draft">Loading draft…</T>
            )}
          </span>
        </div>
      </div>
    );
  }
  return (
    <HtmlWYSIWYGEditor
      key={draftId}
      initialHtml={htmlBody}
      onChange={onChangeHtml}
      height={320}
    />
  );
}

function DraftIntake({
  view,
  userPrompt,
  onUserPromptChange,
  onPickDescribe,
  onBack,
  onAuto,
  onDescribe,
  mailboxReady,
}: {
  view: 'choice' | 'describe';
  userPrompt: string;
  onUserPromptChange: (v: string) => void;
  onPickDescribe: () => void;
  onBack: () => void;
  onAuto: () => void;
  onDescribe: () => void;
  mailboxReady: boolean;
}) {
  const gt = useGT();
  if (view === 'choice') {
    return (
      <div className="rounded-md border bg-muted/20 px-4 py-6 space-y-4">
        <div className="text-sm text-muted-foreground">
          <T context="prompt, how to start drafting the email">
            How would you like to start this email?
          </T>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onAuto}
            disabled={!mailboxReady}
            className="flex-1 justify-start h-auto py-2.5"
          >
            <SparkleIcon className="h-4 w-4 mr-2 shrink-0" />
            <span className="text-left">
              <span className="block text-sm font-medium">
                <T context="button title, let AI auto-draft the email">
                  Auto-draft for me
                </T>
              </span>
              <span className="block text-xs text-muted-foreground font-normal">
                <T context="button subtitle, auto-draft">
                  AI reads the thread and writes a fitting reply.
                </T>
              </span>
            </span>
          </Button>
          <Button
            variant="outline"
            onClick={onPickDescribe}
            disabled={!mailboxReady}
            className="flex-1 justify-start h-auto py-2.5"
          >
            <PaperPlaneTiltIcon className="h-4 w-4 mr-2 shrink-0" />
            <span className="text-left">
              <span className="block text-sm font-medium">
                <T context="button title, describe the email to draft">
                  I&apos;ll describe it
                </T>
              </span>
              <span className="block text-xs text-muted-foreground font-normal">
                <T context="button subtitle, describe the email">
                  Tell the AI what to say; it uses the thread evidence.
                </T>
              </span>
            </span>
          </Button>
        </div>
      </div>
    );
  }
  const canDraft = userPrompt.trim().length > 0 && mailboxReady;
  return (
    <div className="rounded-md border bg-muted/20 px-4 py-4 space-y-2">
      <Label className="text-xs">
        <T context="form label, describe the email to draft">
          What should this email do?
        </T>
      </Label>
      <TextInput
        value={userPrompt}
        onValueChange={onUserPromptChange}
        rows={3}
        autoFocus
        placeholder={gt(
          'e.g. Ask the customer to confirm the freight deduction and send the signed POD',
          { context: 'placeholder, describe email intent' },
        )}
      />
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={onBack}>
          <T context="button, go back to draft mode choice">Back</T>
        </Button>
        <Button size="sm" onClick={onDescribe} disabled={!canDraft}>
          <SparkleIcon className="h-4 w-4 mr-1" />
          <T context="button, draft the email from the description">
            Draft email
          </T>
        </Button>
      </div>
    </div>
  );
}

function RewriteFeedbackRow({
  userPrompt,
  onUserPromptChange,
  onRewrite,
  rememberNote,
  onRememberNoteChange,
  canRemember,
  isBusy,
}: {
  userPrompt: string;
  onUserPromptChange: (v: string) => void;
  onRewrite: () => void;
  rememberNote: boolean;
  onRememberNoteChange: (v: boolean) => void;
  canRemember: boolean;
  isBusy: boolean;
}) {
  const gt = useGT();
  const hasFeedback = userPrompt.trim().length > 0;
  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2 space-y-2">
      <Label className="text-xs">
        <T context="form label, give the AI feedback to rewrite the draft">
          Rewrite with custom feedback
        </T>
      </Label>
      <TextInput
        value={userPrompt}
        onValueChange={onUserPromptChange}
        rows={2}
        placeholder={gt(
          'e.g. ask the customer to confirm the short-pay reason',
          {
            context: 'placeholder, user prompt for ai draft',
          },
        )}
        disabled={isBusy}
        onAction={onRewrite}
        actionDisabled={!hasFeedback}
        actionLabel={
          <>
            <SparkleIcon className="h-4 w-4 mr-1" />
            <T context="button, rewrite the email draft using the typed feedback">
              Rewrite with feedback
            </T>
          </>
        }
      />
      {canRemember && (
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <Checkbox
            checked={rememberNote}
            onCheckedChange={(v) => onRememberNoteChange(v === true)}
            disabled={isBusy || !hasFeedback}
          />
          <T context="checkbox, remember regenerate prompt as customer comms note">
            Remember this as a standing note for this customer
          </T>
        </label>
      )}
    </div>
  );
}

function ExternalRecipientsBlockedBanner({
  offenders,
  allowedDomains,
}: {
  offenders: string[];
  allowedDomains: string[];
}) {
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive flex items-start gap-2">
      <WarningIcon className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="space-y-0.5">
        <div className="font-semibold">
          <T context="error banner, external recipients blocked by feature gate">
            External recipients blocked
          </T>
        </div>
        <div>
          <T context="error banner detail, list of disallowed recipients and allowed domains">
            Cannot send to <Var>{offenders.join(', ')}</Var>. While this feature
            is rolling out, sends are limited to:{' '}
            <Var>{allowedDomains.join(', ')}</Var>.
          </T>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function splitEmails(csv: string): string[] {
  return csv
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Typed (email, role) constructor so callers get `role` inferred as
 * `RecipientRole` rather than widened to `string`, without an `as const` cast.
 */
function recipientEntry(
  email: string,
  role: RecipientRole,
): { email: string; role: RecipientRole } {
  return { email, role };
}

/**
 * Build a deduped list of custom recipients from (email, role) entries.
 * Applies the same EMAIL_REGEX guard as manual entry so a malformed seed can't
 * slip through, and dedups by email (first occurrence wins) so an address
 * claimed by an earlier role beats a later same-address entry in another role.
 */
function buildCustomRecipients(
  entries: { email: string; role: RecipientRole }[],
): CustomRecipient[] {
  const seen = new Set<string>();
  const out: CustomRecipient[] = [];
  for (const { email, role } of entries) {
    const trimmed = email.trim();
    if (!trimmed || !EMAIL_REGEX.test(trimmed)) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ id: makeRecipientId(), email: trimmed, role });
  }
  return out;
}

function collectRecipientCsv(
  selectedEmails: Map<string, RecipientRole>,
  customRecipients: CustomRecipient[],
  bccSelf: string | null,
): { toCsv: string; ccCsv: string; bccCsv: string } {
  // Single email → role map with TO > CC > BCC precedence so the same address
  // can't appear in two roles. The panel mostly prevents this at entry, but
  // mid-stream edits or seeded recipients can drift — cheap to be defensive.
  const final = new Map<string, RecipientRole>();
  function add(rawEmail: string, role: RecipientRole) {
    const email = rawEmail.trim();
    if (!email) return;
    const key = email.toLowerCase();
    const existing = final.get(key);
    if (existing && roleRank(existing) <= roleRank(role)) return;
    final.set(key, role);
  }
  for (const [email, role] of selectedEmails) add(email, role);
  for (const r of customRecipients) add(r.email, r.role);
  if (bccSelf) add(bccSelf, 'bcc');

  const toList: string[] = [];
  const ccList: string[] = [];
  const bccList: string[] = [];
  for (const [email, role] of final) {
    if (role === 'to') toList.push(email);
    else if (role === 'cc') ccList.push(email);
    else bccList.push(email);
  }
  return {
    toCsv: toList.join(', '),
    ccCsv: ccList.join(', '),
    bccCsv: bccList.join(', '),
  };
}

function roleRank(role: RecipientRole): number {
  return role === 'to' ? 0 : role === 'cc' ? 1 : 2;
}

/**
 * Return any To/CC/BCC recipients whose @domain isn't in `allowedDomains`.
 * `allowedDomains` is expected to be normalized (lowercase, no leading "@").
 * An empty allow-list is treated as "everything is external" — matching the
 * server's `assertRecipientsInternalOnly` so the gate fails closed.
 */
function collectExternalOffenders(
  toCsv: string,
  ccCsv: string,
  bccCsv: string,
  allowedDomains: string[],
): string[] {
  const all = [
    ...splitEmails(toCsv),
    ...splitEmails(ccCsv),
    ...splitEmails(bccCsv),
  ];
  const offenders: string[] = [];
  for (const email of all) {
    const at = email.lastIndexOf('@');
    const domain = at >= 0 ? email.slice(at + 1).toLowerCase() : '';
    if (!domain || !allowedDomains.includes(domain)) offenders.push(email);
  }
  return offenders;
}
